import { FunctionComponent, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { OtherSender, senderConfirmationQuestion } from "../utils/senderConfirmation.util";

/**
 * Above the lead panel (z 150 on the web, 1500 in the extension, where the modal portals into the
 * panel's shadow root) and the composer's own popovers — the same tier as the blacklist override
 * that the same Send can raise.
 */
export const SENDER_CONFIRM_Z_INDEX = 1600;

interface SenderConfirmModalProps {
	others: OtherSender[];
	messageCount: number;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Asks before a message goes out as someone other than the current user (PREC-4312): "Send this
 * message as Anna Müller (anna@example.com)?", with Send and Cancel. Several other senders in one
 * batch are listed, each once. There is deliberately no "don't ask again". It takes focus, so
 * Escape (and the backdrop) cancel and Enter sends.
 */
export const SenderConfirmModal: FunctionComponent<SenderConfirmModalProps> = ({
	others,
	messageCount,
	onConfirm,
	onCancel,
}) => {
	const { t } = useTranslation();
	const question = senderConfirmationQuestion(others, messageCount);

	// Unmounted unanswered (its host went away, e.g. a tab switch): that is a Cancel, so the gate
	// is free and the next Send asks again. After an answer the gate ignores it.
	const onCancelRef = useRef(onCancel);

	onCancelRef.current = onCancel;
	useEffect(() => () => onCancelRef.current(), []);

	// Enter answers Send. A modified or held-down Enter (the composer's Ctrl+Enter, pressed again)
	// answers nothing, and Enter on a focused button is left to the button itself. The target is
	// read through the composed path: in the extension's shadow root `event.target` is the host, so
	// Enter on a focused Cancel used to confirm the send.
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			const target = getComposedEventTarget(event) as HTMLElement | null;
			const onButton = target?.tagName === "BUTTON";
			const modified = event.ctrlKey || event.metaKey || event.altKey || event.shiftKey;

			if (event.key === "Enter" && !modified && !event.repeat && !onButton) {
				event.preventDefault();
				onConfirm();
			}
		};

		document.addEventListener("keydown", onKeyDown);

		return () => document.removeEventListener("keydown", onKeyDown);
	}, [onConfirm]);

	return (
		<Modal
			isOpened
			handleClose={onCancel}
			handleSave={onConfirm}
			takeFocus
			size="xs"
			submitButtonText={t("inbox.identity.confirmSend")}
			title={t("inbox.identity.confirmTitle")}
			zIndex={SENDER_CONFIRM_Z_INDEX}
		>
			<div className="flex flex-col gap-2 text-sm text-text" data-testid="sender-confirm-modal">
				<p>{t(question.key, question.values)}</p>
				{others.length > 1 && (
					<ul className="flex flex-col gap-1" data-testid="sender-confirm-list">
						{others.map((other) => (
							<li key={other.key} className="rounded-lg border border-border-light px-3 py-1.5">
								{other.description}
							</li>
						))}
					</ul>
				)}
			</div>
		</Modal>
	);
};
